// engagement-components/EngagementDashboard.jsx
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Badge, Button, Spinner } from "react-bootstrap";
import { FaWhatsapp, FaSms, FaEnvelope, FaEdit, FaPause, FaPlay, FaPlus, FaArrowRight } from "react-icons/fa";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { apiUrl, authHeaders } from "./config";

dayjs.extend(relativeTime);

const C = {
  brand: "#4f46e5", brandLt: "#eef2ff",
  green: "#059669", greenLt: "#ecfdf5",
  text: "#111827", muted: "#6b7280", subtle: "#9ca3af",
  border: "#e5e7eb", bg: "#f9fafb", white: "#ffffff",
};

const statusColors = {
  Active:    { bg: "#dcfce7", color: "#15803d" },
  Paused:    { bg: "#f3f4f6", color: "#6b7280" },
  Completed: { bg: "#dbeafe", color: "#1d4ed8" },
  Scheduled: { bg: "#fef9c3", color: "#92400e" },
  Draft:     { bg: "#f9fafb", color: "#9ca3af" },
};

const StatusPill = ({ status }) => {
  const s = statusColors[status] || statusColors.Draft;
  return (
    <span style={{ fontSize: "0.67rem", fontWeight: 700, padding: "3px 9px",
      borderRadius: 20, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>
      {status}
    </span>
  );
};

const ChannelIcon = ({ channel }) => {
  if (channel === "WhatsApp") return <FaWhatsapp style={{ color: "#25d366", marginRight: 5 }} />;
  if (channel === "SMS")      return <FaSms      style={{ color: "#6c757d", marginRight: 5 }} />;
  return <FaEnvelope style={{ color: "#0d6efd", marginRight: 5 }} />;
};

const STAT_CONFIG = [
  { key: "totalSent",     label: "Messages Sent",   fmt: (v) => Number(v||0).toLocaleString(),              icon: "fa-paper-plane",   grad: ["#4f46e5","#818cf8"] },
  { key: "deliveryRate",  label: "Delivery Rate",   fmt: (v) => `${v||0}%`,                                  icon: "fa-check-double",  grad: ["#059669","#34d399"] },
  { key: "clickRate",     label: "Click Rate",      fmt: (v) => `${v||0}%`,                                  icon: "fa-mouse-pointer", grad: ["#0891b2","#38bdf8"] },
  { key: "conversions",   label: "Conversions",     fmt: (v) => Number(v||0).toLocaleString(),              icon: "fa-exchange-alt",  grad: ["#d97706","#fbbf24"] },
  { key: "revenue",       label: "Revenue",         fmt: (v) => `₹${((Number(v)||0)/1000).toFixed(1)}K`,    icon: "fa-rupee-sign",    grad: ["#be185d","#f472b6"] },
  { key: "activeJourneys",label: "Active Journeys", fmt: (v) => String(v||0),                               icon: "fa-route",         grad: ["#7c3aed","#a78bfa"] },
];

const FUNNEL_COLORS = ["#4f46e5","#0891b2","#059669","#d97706"];

const EngagementDashboard = ({ onNavigate }) => {
  const [stats,     setStats]     = useState(null);
  const [journeys,  setJourneys]  = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [jRes, cRes] = await Promise.all([
        axios.get(`${apiUrl}/feedback/journeys`,  authHeaders()),
        axios.get(`${apiUrl}/feedback/campaigns`, authHeaders()),
      ]);
      const journeyData  = (jRes.data || []).map((j) => ({
        ...j, id: j.journeyid || j.id,
        trigger: j.trigger_type,
        steps:   j.steps_data?.length || 0,
      }));
      const campaignData = (cRes.data || []).map((c) => ({
        ...c, id: c.campaignid || c.id,
        channel:     c.fallback?.channels?.[0] || "WhatsApp",
        audience:    c.audience_count,
        conversions: c.converted,
        revenue:     c.revenue_actual,
        createdAt:   c.created_at,
      }));
      setJourneys(journeyData.slice(0, 4));
      setCampaigns(campaignData.slice(0, 5));
      const totalSent      = campaignData.reduce((s, c) => s + (c.sent      || 0), 0);
      const totalDelivered = campaignData.reduce((s, c) => s + (c.delivered || 0), 0);
      const totalClicked   = campaignData.reduce((s, c) => s + (c.clicked   || 0), 0);
      const totalConverted = campaignData.reduce((s, c) => s + (c.converted || 0), 0);
      const totalRevenue   = campaignData.reduce((s, c) => s + Number(c.revenue_actual || 0), 0);
      setStats({
        totalSent,
        deliveryRate:   totalSent > 0 ? ((totalDelivered / totalSent) * 100).toFixed(1) : 0,
        clickRate:      totalDelivered > 0 ? ((totalClicked / totalDelivered) * 100).toFixed(1) : 0,
        conversions:    totalConverted,
        revenue:        totalRevenue,
        activeJourneys: journeyData.filter((j) => j.status === "Active").length,
      });
    } catch {
      setStats({ totalSent:0, deliveryRate:0, clickRate:0, conversions:0, revenue:0, activeJourneys:0 });
      setJourneys([]);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
      justifyContent:"center", minHeight: 320, gap: 14 }}>
      <Spinner animation="border" style={{ color: C.brand, width:36, height:36 }} />
      <div style={{ fontSize:"0.84rem", color: C.muted }}>Loading dashboard…</div>
    </div>
  );

  const funnel = [
    { stage: "Sent",      count: stats?.totalSent || 0 },
    { stage: "Delivered", count: Math.round((stats?.totalSent||0) * (stats?.deliveryRate||0) / 100) },
    { stage: "Clicked",   count: Math.round((stats?.totalSent||0) * (stats?.clickRate||0)    / 100) },
    { stage: "Converted", count: stats?.conversions || 0 },
  ];
  const maxCount = funnel[0].count || 1;

  return (
    <div style={{ padding: "28px 32px", background: "#f8f9fb", minHeight: "100vh" }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
        <div>
          <h2 style={{ fontSize:"1.55rem", fontWeight:800, color:C.text,
            letterSpacing:"-0.03em", margin:0, lineHeight:1.2 }}>
            Engagement Dashboard
          </h2>
          <p style={{ fontSize:"0.84rem", color:C.muted, margin:"4px 0 0" }}>
            Your guest communication at a glance
          </p>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={() => onNavigate("journeys-create")}
            style={{ display:"inline-flex", alignItems:"center", gap:7,
              padding:"9px 18px", borderRadius:10, border:`1.5px solid ${C.brand}`,
              background: C.white, color:C.brand, fontSize:"0.84rem",
              fontWeight:700, cursor:"pointer" }}>
            <FaPlus size={11} /> Journey
          </button>
          <button onClick={() => onNavigate("campaigns-create")}
            style={{ display:"inline-flex", alignItems:"center", gap:7,
              padding:"9px 18px", borderRadius:10, border:"none",
              background: C.brand, color: C.white, fontSize:"0.84rem",
              fontWeight:700, cursor:"pointer",
              boxShadow:"0 4px 14px rgba(79,70,229,0.35)" }}>
            <FaPlus size={11} /> Campaign
          </button>
        </div>
      </div>

      {/* ── Stat cards ─────────────────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:14, marginBottom:28 }}>
        {STAT_CONFIG.map((s) => {
          const val = STAT_CONFIG.indexOf(s) >= 0 ? stats?.[s.key] : 0;
          return (
            <div key={s.key} style={{
              background: C.white, borderRadius:14, padding:"18px 16px",
              boxShadow:"0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)",
              position:"relative", overflow:"hidden",
            }}>
              {/* Gradient accent top bar */}
              <div style={{ position:"absolute", top:0, left:0, right:0, height:3,
                background:`linear-gradient(90deg, ${s.grad[0]}, ${s.grad[1]})`,
                borderRadius:"14px 14px 0 0" }} />
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <span style={{ fontSize:"0.64rem", fontWeight:800, color:C.subtle,
                  textTransform:"uppercase", letterSpacing:"0.07em" }}>
                  {s.label}
                </span>
                <div style={{ width:28, height:28, borderRadius:8, flexShrink:0,
                  background:`linear-gradient(135deg, ${s.grad[0]}, ${s.grad[1]})`,
                  display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <i className={`fas ${s.icon}`} style={{ color:"#fff", fontSize:"0.72rem" }} />
                </div>
              </div>
              <div style={{ fontSize:"1.5rem", fontWeight:800, color:C.text,
                letterSpacing:"-0.03em", lineHeight:1 }}>
                {s.fmt(val)}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Funnel + Journeys ──────────────────────────────────────────────── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:18, marginBottom:18 }}>

        {/* Funnel */}
        <div style={{ background:C.white, borderRadius:16, padding:"22px 24px",
          boxShadow:"0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
            <div style={{ fontSize:"0.88rem", fontWeight:800, color:C.text }}>
              Engagement Funnel
            </div>
          </div>
          {funnel.map((item, i) => {
            const pct = ((item.count / maxCount) * 100);
            return (
              <div key={i} style={{ marginBottom: i < funnel.length-1 ? 16 : 0 }}>
                <div style={{ display:"flex", justifyContent:"space-between",
                  marginBottom:5, alignItems:"center" }}>
                  <span style={{ fontSize:"0.8rem", fontWeight:600, color:C.text }}>{item.stage}</span>
                  <span style={{ fontSize:"0.8rem", fontWeight:700, color: FUNNEL_COLORS[i] }}>
                    {item.count.toLocaleString()}
                    {i > 0 && maxCount > 0 && (
                      <span style={{ fontWeight:400, color:C.subtle, marginLeft:4 }}>
                        ({pct.toFixed(1)}%)
                      </span>
                    )}
                  </span>
                </div>
                <div style={{ height:8, borderRadius:99, background:"#f1f5f9", overflow:"hidden" }}>
                  <div style={{
                    height:"100%", borderRadius:99,
                    width: `${pct}%`,
                    background: `linear-gradient(90deg, ${FUNNEL_COLORS[i]}, ${FUNNEL_COLORS[i]}aa)`,
                    transition:"width 0.9s cubic-bezier(0.4,0,0.2,1)",
                    minWidth: item.count > 0 ? 6 : 0,
                  }} />
                </div>
              </div>
            );
          })}
          {funnel[0].count === 0 && (
            <div style={{ textAlign:"center", padding:"16px 0 4px",
              fontSize:"0.8rem", color:C.subtle }}>
              Send a campaign to see funnel data
            </div>
          )}
        </div>

        {/* Active Journeys */}
        <div style={{ background:C.white, borderRadius:16, padding:"22px 24px",
          boxShadow:"0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
            <div style={{ fontSize:"0.88rem", fontWeight:800, color:C.text }}>
              Active Journeys
            </div>
            <button onClick={() => onNavigate("journeys")}
              style={{ border:"none", background:"none", fontSize:"0.78rem",
                fontWeight:600, color:C.brand, cursor:"pointer",
                display:"flex", alignItems:"center", gap:4 }}>
              View all <FaArrowRight size={9} />
            </button>
          </div>

          {journeys.length === 0 ? (
            <div style={{ textAlign:"center", padding:"32px 0" }}>
              <div style={{ fontSize:"2.5rem", marginBottom:10 }}>🔁</div>
              <div style={{ fontSize:"0.92rem", fontWeight:700, color:C.text, marginBottom:6 }}>
                No journeys yet
              </div>
              <p style={{ fontSize:"0.81rem", color:C.muted, marginBottom:16, maxWidth:280, margin:"0 auto 16px" }}>
                Automate birthday wishes, check-in messages, anniversary greetings and more.
              </p>
              <button onClick={() => onNavigate("journeys-create")}
                style={{ padding:"8px 20px", borderRadius:9, border:"none",
                  background:C.brand, color:C.white, fontSize:"0.82rem",
                  fontWeight:700, cursor:"pointer" }}>
                <FaPlus size={10} style={{ marginRight:5 }} />
                Create First Journey
              </button>
            </div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {journeys.map((j) => {
                const isActive = j.status === "Active";
                return (
                  <div key={j.id} style={{
                    padding:"14px 16px", borderRadius:12,
                    border: `1px solid ${isActive ? "#bbf7d0" : C.border}`,
                    background: isActive ? "#f0fdf4" : C.bg,
                    cursor:"pointer", transition:"all 0.15s",
                  }}
                  onClick={() => onNavigate("journeys-edit", j)}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
                    <div style={{ display:"flex", justifyContent:"space-between",
                      alignItems:"flex-start", marginBottom:8 }}>
                      <div style={{ fontSize:"0.84rem", fontWeight:700, color:C.text,
                        lineHeight:1.3, flex:1, paddingRight:8 }}>
                        {j.name}
                      </div>
                      <StatusPill status={j.status} />
                    </div>
                    <div style={{ fontSize:"0.74rem", color:C.muted, marginBottom:8 }}>
                      <i className="fas fa-bolt" style={{ marginRight:4, color: isActive ? C.green : C.subtle, fontSize:"0.64rem" }} />
                      {j.trigger} · {j.steps} step{j.steps !== 1 ? "s" : ""}
                    </div>
                    <div style={{ display:"flex", gap:12 }}>
                      <div>
                        <div style={{ fontSize:"0.62rem", color:C.subtle, marginBottom:1 }}>FIRED</div>
                        <div style={{ fontSize:"0.88rem", fontWeight:700, color:C.text }}>
                          {(j.total_fired || 0).toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize:"0.62rem", color:C.subtle, marginBottom:1 }}>DELIVERED</div>
                        <div style={{ fontSize:"0.88rem", fontWeight:700, color:C.green }}>
                          {(j.total_delivered || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Campaigns ───────────────────────────────────────────────── */}
      <div style={{ background:C.white, borderRadius:16, overflow:"hidden",
        boxShadow:"0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
          padding:"18px 24px", borderBottom:`1px solid ${C.border}` }}>
          <div style={{ fontSize:"0.88rem", fontWeight:800, color:C.text }}>
            Recent Campaigns
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button onClick={() => onNavigate("campaigns")}
              style={{ border:"none", background:"none", fontSize:"0.78rem",
                fontWeight:600, color:C.brand, cursor:"pointer",
                display:"flex", alignItems:"center", gap:4 }}>
              View all <FaArrowRight size={9} />
            </button>
            <button onClick={() => onNavigate("campaigns-create")}
              style={{ padding:"6px 14px", borderRadius:8, border:"none",
                background:C.brand, color:C.white, fontSize:"0.77rem",
                fontWeight:700, cursor:"pointer" }}>
              + New
            </button>
          </div>
        </div>

        {campaigns.length === 0 ? (
          <div style={{ textAlign:"center", padding:"52px 0" }}>
            <div style={{ fontSize:"2.5rem", marginBottom:10 }}>📣</div>
            <div style={{ fontSize:"0.94rem", fontWeight:700, color:C.text, marginBottom:6 }}>
              No campaigns yet
            </div>
            <p style={{ fontSize:"0.82rem", color:C.muted, marginBottom:18 }}>
              Send your first broadcast to guests and start driving revenue.
            </p>
            <button onClick={() => onNavigate("campaigns-create")}
              style={{ padding:"9px 22px", borderRadius:9, border:"none",
                background:C.brand, color:C.white, fontSize:"0.84rem",
                fontWeight:700, cursor:"pointer" }}>
              <FaPlus size={10} style={{ marginRight:5 }} />
              Create Campaign
            </button>
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr style={{ background:"#f8f9fb" }}>
                  {["Campaign","Channel","Audience","Sent","Clicked","Conversions","Revenue","Status"].map((h, i) => (
                    <th key={h} style={{ padding:"10px 20px", textAlign: i>=2 && i<=5 ? "center" : i===6 ? "right" : "left",
                      fontSize:"0.67rem", fontWeight:800, color:C.subtle,
                      textTransform:"uppercase", letterSpacing:"0.06em",
                      borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c, idx) => (
                  <tr key={c.id}
                    style={{ background: idx % 2 === 0 ? C.white : "#fafafa",
                      cursor:"pointer", transition:"background 0.1s" }}
                    onClick={() => onNavigate("campaigns-edit", c)}
                    onMouseEnter={e => { e.currentTarget.style.background = "#f0f4ff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = idx%2===0 ? C.white : "#fafafa"; }}>
                    <td style={{ padding:"13px 20px" }}>
                      <div style={{ fontSize:"0.85rem", fontWeight:700, color:C.text }}>{c.name}</div>
                      <div style={{ fontSize:"0.72rem", color:C.subtle, marginTop:1 }}>
                        {c.createdAt ? dayjs(c.createdAt).format("DD MMM YYYY") : "—"}
                      </div>
                    </td>
                    <td style={{ padding:"13px 20px" }}>
                      <span style={{ fontSize:"0.82rem", fontWeight:600, color:C.text,
                        display:"flex", alignItems:"center" }}>
                        <ChannelIcon channel={c.channel} />{c.channel}
                      </span>
                    </td>
                    <td style={{ padding:"13px 20px", textAlign:"center", fontSize:"0.84rem", color:C.text }}>
                      {(c.audience||0).toLocaleString()}
                    </td>
                    <td style={{ padding:"13px 20px", textAlign:"center", fontSize:"0.84rem",
                      fontWeight:600, color:C.text }}>
                      {(c.sent||0).toLocaleString()}
                    </td>
                    <td style={{ padding:"13px 20px", textAlign:"center", fontSize:"0.84rem",
                      fontWeight:700, color:C.green }}>
                      {(c.clicked||0).toLocaleString()}
                    </td>
                    <td style={{ padding:"13px 20px", textAlign:"center", fontSize:"0.84rem",
                      fontWeight:700, color:C.brand }}>
                      {(c.conversions||0).toLocaleString()}
                    </td>
                    <td style={{ padding:"13px 20px", textAlign:"right", fontSize:"0.84rem",
                      fontWeight:800, color:C.green }}>
                      ₹{Number(c.revenue||0).toLocaleString()}
                    </td>
                    <td style={{ padding:"13px 20px" }}>
                      <StatusPill status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EngagementDashboard;
